import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { THEME } from '@/lib/theme';
import { useT } from '@/lib/i18n/useT';
import type { StationInventoryItem } from '@/lib/api/types';

interface StockWonChartProps {
  inventory: StationInventoryItem[];
  height?: number;
}

export function StockWonChart({ inventory, height = 240 }: StockWonChartProps) {
  const t = useT();
  const data = inventory
    .map((it) => ({
      name: it.stockName,
      used: it.used,
      remaining: it.available,
      total: it.quantity,
    }))
    .sort((a, b) => b.used - a.used);

  if (data.length === 0 || data.every((d) => d.total === 0)) {
    return (
      <p className="py-8 text-center text-sm text-[var(--color-fg-muted)]">
        {t('stations.detail.dashboardEmpty')}
      </p>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 16, bottom: 8, left: 16 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
        <XAxis
          type="number"
          allowDecimals={false}
          tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          dataKey="name"
          type="category"
          width={120}
          tick={{ fontSize: 11, fill: 'var(--color-fg-muted)' }}
          tickLine={false}
        />
        <Tooltip
          cursor={{ fill: 'var(--color-bg-elevated)' }}
          contentStyle={{
            background: 'var(--color-bg-surface)',
            border: '1px solid var(--color-border-default)',
            borderRadius: 8,
            fontSize: 12,
          }}
          formatter={(value: number, key: string) => [
            value,
            key === 'used'
              ? t('stations.detail.usedCol')
              : t('stations.detail.totalRemaining'),
          ]}
        />
        <Bar dataKey="used" stackId="inv" fill={THEME.accent} />
        <Bar
          dataKey="remaining"
          stackId="inv"
          fill={THEME.success}
          fillOpacity={0.35}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}
